import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {hooks} from 'farmx-redux-core';
import { WATER_FLOW_ANALOG } from 'components/hooks/graph';
import {
  getYminMaxForMultipleSeries
} from '../../helper/graphHelper';
import NewSensorDataChart from './NewSensorDataChart';
import { VAR_WATER_FLOW_ANALOG } from './constants';

interface Sensor {
  name?: string;
  id?: number;
  type?: string;
  identifier?: string;
}

interface WaterFlowChartProps {
  sensor?: Sensor | any;
  startDate?: any;
  endDate?: any;
  cached?: string | any;
  updateYminmax?: () => void;
  uniformYAxis?: boolean;
  yMinMaxMap?: any;
  sensorsString?: string | any;
}

const flowRateLabel = { gallons_per_minute: 'GPM'}
const { useUnits } = hooks;

const WaterFlowChart: React.FC<WaterFlowChartProps> = (props: any) => {
  const {
    sensor, startDate, endDate, cached, updateYminmax, uniformYAxis, yMinMaxMap,
    showXaxis, header, sensorsString
  } = props;
  const { t } = useTranslation();
  const getUserUnits = useUnits();

  const globalYMinMax = yMinMaxMap?.[WATER_FLOW_ANALOG]
     ? yMinMaxMap?.[WATER_FLOW_ANALOG] : {min: null, max: null};

  const yMinMax = useMemo(() => {
    if((globalYMinMax.min || globalYMinMax.min===0) && globalYMinMax.max) {
       return { min: globalYMinMax.min, max: globalYMinMax.max};
    }
    return null;
  }, [globalYMinMax.min, globalYMinMax.max]);

  /** ******** Custom Config Section Start*************** */
  const customConfig = useCallback((configObj, seriesData) => {
    if(configObj?.series?.[0]){
      const yMinMax = getYminMaxForMultipleSeries(seriesData) || { min: 0, max: 0 };
      const { multiplier } = configObj?.series?.[0];
      const flowRateData = seriesData?.flow_rate;
      const convertedFlowRateData = flowRateData?.map((item) => {
        const convertedData = item.data?.map((d) => {
          const { value } = getUserUnits(d?.[1], 'gallons_per_minute', 'flow_rate', { decimalPlaces: 2 });
          return [d?.[0], value];
        });
        return {...item, data: convertedData }
      })

      const minMaxArr = convertedFlowRateData?.map((flData) => {
        const dataPoints = flData?.data?.map((d) => d?.[1]) || [];
        const min = Math.min(...dataPoints);
        const max = Math.max(...dataPoints);
        return [min, max]
      })?.flat() || [];

      const yMin = Math.min(...minMaxArr)
      const yMax = Math.max(...minMaxArr)

      updateYminmax(WATER_FLOW_ANALOG, yMin * multiplier, yMin * multiplier);
      const label = getUserUnits(yMin, 'gallons_per_minute', 'flow_rate', { decimalPlaces: 2 })?.label;

      const multiLineSeries = convertedFlowRateData.map((item, index) => {
          const dataPoints = item?.data?.map((d) => d?.[1]) || [];
          const min = Math.min(...dataPoints);
          const max = Math.max(...dataPoints);
          return {
            ...configObj?.series?.[0],
            ...{ data: item?.data },
            sensorName: item?.sensor_name,
            name: (`${item?.name || 'Flow Rate'}: ${convertedFlowRateData
              ?.[index]?.sensor_name || ''}`),
            tooltip: {
              valueDecimals: 3,
              valueSuffix: t(` ${label}`),
            },
            min,
            max,
          }
      })

      return {
        ...configObj,
        series: multiLineSeries,
        yAxis: [{
          ...configObj.yAxis[0],
          min: yMin,
          max: yMax
        }],
      };
    }
    return { ...configObj, ...(!showXaxis ? { xAxis: { visible: false } } : {}) };
  }, [updateYminmax]);

  return (
    <>
      {(sensor?.id || sensorsString) && 
      <NewSensorDataChart
        sensorName={sensor?.name}
        sensorId={sensor?.id}
        sensorType={sensor?.type}
        sensorIdentifier={sensor?.identifier}
        variables={VAR_WATER_FLOW_ANALOG}
        startDate={startDate}
        endDate={endDate}
        cached={cached}
        customConfig={customConfig}
        header={header
          ? {title: t('Water Flow Analog'), units: t(flowRateLabel?.gallons_per_minute), right: []}
          : undefined
        }
        uniformYAxis={uniformYAxis}
        yMinMax={yMinMax}
        sensorsString={sensorsString}
      />}
      {!sensor && !sensorsString && <div className="chart-component-main">
        <div className='chart-loading-container'>
          <div className="chart-loading">
            <span className="chart-loading-inner">{`${t('Sensor Not Available')}`}</span>
          </div>
        </div>
      </div>}
    </>
  );
}

WaterFlowChart.defaultProps = {
  sensor: null,
  startDate: null,
  endDate: null,
  cached: null,
  updateYminmax: () => null,
  uniformYAxis: false,
  yMinMaxMap: null,
  header: true,
  showXaxis: true,
  sensorsString: undefined,
};

export default WaterFlowChart;